/**
 * A TELA SEGUE A LÍNGUA DO USUÁRIO — a preferência persistida (`User.language`,
 * espelhada pelo motor a cada turno) chega em `/auth/status` e é aplicada aqui.
 *
 * Ordem (RAIZ 10, `locale-source-of-truth.test.ts`): escolha EXPLÍCITA local
 * (`USER_LOCALE_KEY`) > preferência persistida do usuário > default pt-BR.
 * Nunca o navegador. A preferência aplicada NÃO vira escolha explícita local:
 * quem mudou de idioma em outra sessão tem de ver a mudança aqui também.
 *
 * Defeito medido (07/09/2026): usuário `en_US` (o motor detectou inglês na
 * primeira mensagem e persistiu) via prosa em inglês e cards em português — a
 * tela nunca lia a preferência e nascia em pt-BR.
 *
 * STO-1401/1420 — SÓ `languageSource === 'selected'` aplica. Até esta entrega
 * QUALQUER `User.language` era tratado como ordem, mesmo `mirrored`/`detected`
 * (o motor só espelhava o idioma AMBIENTE da tela, nunca uma escolha real) —
 * isso pisava no default do TENANT (etapa 2, `i18n/config.ts`) numa corrida
 * contra `reconcileHarnessI18nConfig`, porque um `mirrored` qualquer vencia
 * mesmo sem a pessoa ter escolhido nada. A precedência de 4 níveis do motor
 * (`orchestrator/turn-locale.ts`, STO-1420) só deixa o NÍVEL 2 (`User.language`
 * com origem `selected`) vencer o default do tenant (nível 3); `mirrored` e
 * `detected` ficam ABAIXO dele. Esta função espelha essa regra: origem que não
 * é `selected` não aplica nada — o default do tenant fica de pé.
 */
import i18n from 'i18next'
import { USER_LOCALE_KEY } from './locale-detection'
import { useUIStore } from '../stores/uiStore'

/** Enum cru ou locale efetivo do motor → locale do produto. Fora do produto → null. */
const LANGUAGE_DO_USUARIO_PARA_LOCALE: Readonly<Record<string, string>> = {
  pt_BR: 'pt-BR',
  'pt-BR': 'pt-BR',
  en_US: 'en-US',
  'en-US': 'en-US',
  es: 'es-419',
  'es-419': 'es-419',
}

export function languageDoUsuarioParaLocale(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  return LANGUAGE_DO_USUARIO_PARA_LOCALE[raw] ?? null
}

export interface DepsDoIdioma {
  storage: Pick<Storage, 'getItem'>
  idiomaAtual: () => string
  mudarIdioma: (locale: string) => unknown
  aplicarNaTela: (locale: string) => void
}

/**
 * Deps reais: o SINGLETON do i18next (não `./i18n/config`, que inicializa o
 * i18next ao ser importado e estourava todo teste que mocka `i18next` sem
 * `.use`), e o store da tela. Imports ESTÁTICOS de propósito: um import
 * dinâmico do uiStore fazia o rolldown separar o uiStore num chunk próprio
 * com ciclo — `zustand.create` chegava indefinido e a tela inteira morria no
 * boot com "n is not a function" (galeria-visual do #794, reproduzido e
 * mapeado por sourcemap em `uiStore.ts:30`).
 */
const DEPS_REAIS: DepsDoIdioma = {
  storage: localStorage,
  idiomaAtual: () => i18n.language,
  mudarIdioma: (locale) => i18n.changeLanguage(locale),
  aplicarNaTela: (locale) => useUIStore.setState({ locale }),
}

/** Modo privado / storage bloqueado lança no getItem — sem escolha, então. */
function escolhaLocalExplicita(storage: Pick<Storage, 'getItem'>): string | null {
  try {
    return storage.getItem(USER_LOCALE_KEY)
  } catch {
    return null
  }
}

export type ResultadoDoIdioma =
  | 'aplicado'
  | 'escolha_local_vence'
  | 'fora_do_produto'
  | 'ja_estava'
  | 'nao_e_escolha'

/**
 * Aplica a preferência persistida à tela. Devolve o que fez — quem chama loga
 * se quiser; nunca lança (a tela não pode quebrar por causa de idioma).
 *
 * @param languageSource - `User.languageSource` cru (`unset | detected |
 *   mirrored | selected`, STO-1420). Qualquer valor diferente de `'selected'`
 *   devolve `'nao_e_escolha'` sem tocar na tela — só escolha EXPLÍCITA da
 *   pessoa vence o default do tenant.
 */
export async function aplicarIdiomaDoUsuario(
  languageDoUsuario: unknown,
  languageSource: unknown,
  deps: DepsDoIdioma = DEPS_REAIS,
): Promise<ResultadoDoIdioma> {
  const locale = languageDoUsuarioParaLocale(languageDoUsuario)
  if (locale === null) return 'fora_do_produto'
  if (languageSource !== 'selected') return 'nao_e_escolha'
  if (escolhaLocalExplicita(deps.storage) !== null) return 'escolha_local_vence'
  if (deps.idiomaAtual() === locale) return 'ja_estava'
  await deps.mudarIdioma(locale)
  deps.aplicarNaTela(locale)
  return 'aplicado'
}
